import React, { useMemo } from 'react';
import { ClientFeature } from '@frontend/app/constants/clientFeatures';
import { TProject } from '@frontend/app/containers/Projects/types';
import { useClientFeatures } from '@frontend/context/ClientFeatureContext';
import ContentGuidelines from '@frontend/applications/TermsApp/containers/ContentGuidelines/ContentGuidelines';
import { useAuth } from '@frontend/context/authContext';
import { useGetDefaultProgramTemplate } from '@frontend/app/hooks';
import { TermsType } from '@frontend/app/types/globalTypes';
import BasicAdvancedTermsSettingsPage from './BasicAdvancedTermsSettingsPage';
import TermsSettingsPage from './TermsSettingsPage';
import { SidebarLayout } from '../components/SidebarLayout';
import BriefEmailTemplates from './BriefEmailTemplates';

interface ITermsSettingsWrapperProps {
  project: TProject;
}

export const TermsSettingsWrapper: React.FC<ITermsSettingsWrapperProps> = ({ project }) => {
  const {
    [ClientFeature.BASIC_TERMS]: isBasicTermsEnabled,
    [ClientFeature.FLEXIBLE_PROJECT]: isFlexibleProjectEnabled,
  } = useClientFeatures();

  const { clientInfo } = useAuth();

  const {
    data: defaultProgramTemplate,
  } = useGetDefaultProgramTemplate({
    variables: {
      programId: project.id,
    },
    skip: !project.id,
    fetchPolicy: 'no-cache',
  });

  const sections = useMemo(() => [
    {
      id: 'default-brief',
      label: 'Default Brief',
      component: <BasicAdvancedTermsSettingsPage project={project} showFlexUI />,
    },
    {
      id: 'brief-email-template',
      label: 'Brief Email Template',
      component: <BriefEmailTemplates projectId={project.id} program={project} />,
    },
    ...(
      isBasicTermsEnabled && defaultProgramTemplate?.defaultTemplate?.termsType === TermsType.BASIC ?
        [
          {
            id: 'content-guidelines',
            label: 'Content Guidelines',
            component: <ContentGuidelines clientId={clientInfo?.id} projectId={project.id} showFlexUI />,
          }
        ] :
        []
    ),
  ], [clientInfo?.id, isBasicTermsEnabled, project, defaultProgramTemplate?.defaultTemplate?.termsType]);

  if (isFlexibleProjectEnabled) {
    return (
      <SidebarLayout
        sections={sections}
        activeSectionId="default-brief"
        onlyShowActiveSection
      />
    );
  }

  return isBasicTermsEnabled ? (
    <BasicAdvancedTermsSettingsPage project={project} />
  ) : (
    <TermsSettingsPage project={project} />
  );
};
